import React, { Component, useState, useEffect, useContext, createContext } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import PropTypes from 'prop-types'

import Card from '../Card'
import DndBlock from '../dnd/Block'

const propTypes = {
  data: PropTypes.object,
  block: PropTypes.bool,
}

export default class BaseItem extends Component {
  accept = []
  icon = null
  title = ""
  multipleChildren = false
  type = null
  model = undefined

  useItemDrag = ({ key, value }) => {
    const { props: { component: { type, data }, path, id } } = this

    const [{ isDragging }, dragReference] = useDrag(() => ({
      type,
      item: { path, id, type, key, value, data },
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      })
    }))

    return { dragReference, isDragging }
  }

  useExternalDropzone = accept => {
    const { props: { path, id, handleSetTree } } = this

    const [{ externalIsOver }, externalDropReference] = useDrop(() => ({
      accept,
      drop(item, monitor) {
        const action = item.path() === 'pallet' ? 'add' : 'move'
        let from = item.path()
        let fromPosition = item.id
        let to = path
        let toPosition = id

        handleSetTree(action, { from, fromPosition, to, toPosition, item, extra: 'addToPosition' })
      },
      canDrop(item, monitor) {
        if (item.path() === path && item.id === id - 1) return false
        return true
      },
      collect: (monitor) => ({
        externalIsOver: monitor.isOver()
      })
    }))

    return { externalDropReference, externalIsOver }
  }

  useItemDropzone = accept => {
    const { props: { path, id, handleSetTree } } = this

    const [{ itemIsOver, itemCanDrop }, itemDropReference] = useDrop(() => ({
      accept,
      drop(item, monitor) {
        const action = item.path() === 'pallet' ? 'add' : 'move'
        let from = item.path()
        let fromPosition = item.id
        let to = path
        let toPosition = id

        handleSetTree(action, { from, fromPosition, to, toPosition, item, extra: 'addReplacing' })
      },
      collect: (monitor) => ({
        itemIsOver: monitor.isOver(),
        itemCanDrop: monitor.canDrop(),
      })
    }))

    return { itemDropReference, itemIsOver, itemCanDrop }
  }

  useProperty = () => {
    const { updateItem, component } = this.props

    const [key, setKey] = useState('')
    const [value, setValue] = useState('')

    useEffect(() => {
      if (component.key && component.key !== key) {
        setKey(component.key)
      }
    }, [component.key])

    useEffect(() => {
      if (component.value && component.value !== value) {
        setValue(component.value)
      }
    }, [component.value])

    return {
      key, value,
      setValue: value => {
        setValue(value)
        updateItem({ value })
      },
      setKey: key => {
        setKey(key)
        updateItem({ key })
      }
    }
  }

  useInternalDropzone = () => {
    const { accept, props: { component: { data }, path, id, handleSetTree } } = this

    const [{ internalIsOver, internalCanDrop }, internalDropReference] = useDrop(() => ({
      accept,
      drop(item, monitor) {
        const action = item.path() === 'pallet' ? 'add' : 'move'
        const from = item.path()
        const fromPosition = item.id
        const to = `${path}-${id}`
        const toPosition = data ? data.length : null

        handleSetTree(action, { from, fromPosition, to, toPosition, item, extra: 'addLastPosition' })
      },
      collect: (monitor) => ({
        internalIsOver: monitor.isOver(),
        internalCanDrop: monitor.canDrop(),
      })
    }))

    return { internalDropReference, internalIsOver, internalCanDrop }
  }

  Item = () => {
    const { icon, title, accept, multipleChildren, props: { component: { collapse = true }, path, id, handleSetTree, updateItem } } = this

    const { accept: parentAccept, multipleChildren: parentMultipleChildren } = useContext(Parent)

    const property = this.useProperty()
    const internalDropzone = this.useInternalDropzone()
    const itemDrag = this.useItemDrag(property)
    const externalDropzone = this.useExternalDropzone(parentAccept)
    const itemDropzone = this.useItemDropzone(parentAccept)

    const setIsOpen = () => {
      updateItem({ collapse: !collapse })
    }

    return <Card
      id={id}
      path={path}
      canHaveManyChildren={!!multipleChildren}
      head={{ icon, title, subtitle: property.key, isOpen: collapse, setIsOpen }}
      handleSetTree={handleSetTree}
      drag={itemDrag}
      parentMultipleChildren={!!parentMultipleChildren}
      externalDropzone={externalDropzone}
      itemDropzone={itemDropzone}
      internalDropzone={internalDropzone}
    >
      <Parent.Provider value={{ accept, multipleChildren }}>
        <this.ItemContent {...{ property, internalDropzone }} />
      </Parent.Provider>
    </Card>
  }

  ItemContent = () => {
    const { props: { children } } = this

    return children
  }

  PaletteItem = () => {
    const { title, icon, type, model } = this
    return <DndBlock type={type} model={model} title={title} icon={icon} />
  }

  render() {
    const { props: { block } } = this
    if (block)
      return <this.PaletteItem />

    return <this.Item />
  }
}

const Parent = createContext({ accept: [], multipleChildren: false })

BaseItem.propTypes = propTypes
BaseItem.defaultProps = {
  block: false
}